import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Users, Layers, Plus, X, Loader2, CreditCard, Trash2 } from "lucide-react";

const ROLES = ["company_admin", "sales_manager", "sales_executive", "accounts_manager", "operations_executive", "support_manager", "support_executive", "employee"];

function UsageBar({ label, value, max }) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div data-testid={`usage-${label.toLowerCase()}`}>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{value} / {max}</span>
      </div>
      <Progress value={pct} className={`h-2 ${pct >= 90 ? "[&>div]:bg-critical" : ""}`} />
    </div>
  );
}

export default function Settings() {
  const qc = useQueryClient();
  const { tenant, refresh, user } = useAuth();
  const isAdmin = ["company_owner", "company_admin"].includes(user?.role);
  const [newBU, setNewBU] = useState("");
  const [userOpen, setUserOpen] = useState(false);
  const [uf, setUf] = useState({ name: "", email: "", role: "employee", team: "" });

  const { data: tData, isLoading } = useQuery({ queryKey: ["tenant"], queryFn: async () => (await api.get("/tenant")).data });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });

  const settingsMut = useMutation({
    mutationFn: (settings) => api.put("/tenant/settings", settings),
    onSuccess: async () => { await refresh(); qc.invalidateQueries({ queryKey: ["tenant"] }); toast.success("Settings updated"); },
  });
  const userMut = useMutation({
    mutationFn: (body) => api.post("/users", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); setUserOpen(false); setUf({ name: "", email: "", role: "employee", team: "" }); toast.success("Team member added (temp password: Welcome@123)"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to add member"),
  });
  const deleteUserMut = useMutation({
    mutationFn: (id) => api.delete(`/users/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Team member removed"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to remove member"),
  });

  if (isLoading || !tData) return <div><PageHeader title="Settings" /><Loading /></div>;
  const t = tData.tenant;
  const bus = t.settings?.business_units || [];

  const saveBUs = (list) => settingsMut.mutate({ ...t.settings, business_units: list });

  return (
    <div>
      <PageHeader title="Settings" subtitle={`Manage ${t.name}`} testid="settings-header" />
      <Tabs defaultValue="company">
        <TabsList className="flex-wrap">
          <TabsTrigger value="company" data-testid="tab-company">Company</TabsTrigger>
          <TabsTrigger value="plan" data-testid="tab-plan">Plan & Usage</TabsTrigger>
          <TabsTrigger value="units" data-testid="tab-units">Business Units</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-4">
          <Card className="max-w-2xl space-y-4 p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Building2 className="h-6 w-6" /></span>
              <div><p className="font-heading text-lg font-bold">{t.name}</p><p className="text-sm text-muted-foreground">{t.industry} · {t.country}</p></div>
            </div>
            <div className="grid grid-cols-2 gap-4 border-t border-border pt-4 text-sm">
              <div><p className="text-muted-foreground">Employees</p><p className="font-medium">{t.employees}</p></div>
              <div><p className="text-muted-foreground">GSTIN</p><p className="font-medium">{t.gstin || "—"}</p></div>
              <div><p className="text-muted-foreground">Template</p><p className="font-medium">{t.template}</p></div>
              <div><p className="text-muted-foreground">Currency</p><p className="font-medium">{t.settings?.currency || "INR"}</p></div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="plan" className="mt-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Current plan</p>
                  <p className="font-heading text-2xl font-bold capitalize">{t.plan}</p>
                </div>
                <Badge className="capitalize" variant={t.subscription_status === "trial" ? "secondary" : "default"}>{t.subscription_status}</Badge>
              </div>
              <Button className="mt-4 w-full rounded-full" data-testid="upgrade-plan-btn"><CreditCard className="mr-1.5 h-4 w-4" /> Upgrade plan</Button>
            </Card>
            <Card className="space-y-4 p-6">
              <p className="font-heading text-sm font-semibold">Usage</p>
              <UsageBar label="Users" value={tData.usage.users} max={t.limits?.users || 15} />
              <UsageBar label="Leads" value={tData.usage.leads} max={t.limits?.leads || 5000} />
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="units" className="mt-4">
          <Card className="max-w-2xl p-6">
            <div className="mb-4 flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" /><h3 className="font-heading text-base font-semibold">Business Units</h3>
            </div>
            <div className="space-y-2">
              {bus.map((b) => (
                <div key={b} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid={`bu-${b}`}>
                  <span className="text-sm font-medium">{b}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => saveBUs(bus.filter((x) => x !== b))} data-testid={`remove-bu-${b}`}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Input value={newBU} onChange={(e) => setNewBU(e.target.value)} placeholder="Add business unit" data-testid="new-bu-input" />
              <Button onClick={() => { if (newBU.trim()) { saveBUs([...bus, newBU.trim()]); setNewBU(""); } }} disabled={settingsMut.isPending} data-testid="add-bu-btn"><Plus className="h-4 w-4" /></Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="team" className="mt-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{users.length} team members</p>
            <Dialog open={userOpen} onOpenChange={setUserOpen}>
              <DialogTrigger asChild><Button className="rounded-full" data-testid="add-member-btn"><Plus className="mr-1.5 h-4 w-4" /> Add Member</Button></DialogTrigger>
              <DialogContent><DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1.5"><Label>Name</Label><Input value={uf.name} onChange={(e) => setUf({ ...uf, name: e.target.value })} data-testid="member-name-input" /></div>
                  <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={uf.email} onChange={(e) => setUf({ ...uf, email: e.target.value })} data-testid="member-email-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Role</Label>
                      <Select value={uf.role} onValueChange={(v) => setUf({ ...uf, role: v })}><SelectTrigger data-testid="member-role-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div className="space-y-1.5"><Label>Team</Label><Input value={uf.team} onChange={(e) => setUf({ ...uf, team: e.target.value })} placeholder="Sales" /></div>
                  </div>
                  <DialogFooter><Button onClick={() => userMut.mutate(uf)} disabled={userMut.isPending || !uf.name || !uf.email} data-testid="member-save-btn">{userMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add member"}</Button></DialogFooter>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="divide-y divide-border">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between p-4" data-testid={`settings-member-${u.id}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">{u.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}</span>
                  <div><p className="text-sm font-medium">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="capitalize">{u.role?.replace(/_/g, " ")}</Badge>
                  {isAdmin && u.id !== user?.id && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteUserMut.mutate(u.id)} data-testid={`remove-member-${u.id}`}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
